import { bodyPresent, type BodyPoint, type BodyPose } from './bodyPoseFrame.ts';

/**
 * Логика меню headless-ТВ (GEN-18912) без DOM: курсор по руке, попадание с гистерезисом, dwell-нажатие,
 * D-pad-навигация и коды кнопок пульта. Всё в долях экрана 0…1, y вниз.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MenuItem {
  id: string;
  rect: Rect;
}

/** Удержание руки на кнопке до нажатия. */
export const DWELL_MS = 1000;

/** Наведённая кнопка «держит» руку в рамке шире на эту долю своего размера — без дрожания на границе. */
export const HOVER_HYSTERESIS = 0.08;

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * Рука-курсор: правая, если видна, иначе левая; кончик указательного точнее запястья. Позиция — в «зоне досягаемости»
 * вокруг плеч (полтора плеча в стороны), чтобы до краёв экрана хватало вытянутой руки, а не шага в сторону.
 * `null` — руки не видно или человека в кадре нет (одиночная рука без плеч — шум детектора на пустой камере,
 * она скачет по экрану).
 */
export function cursorFromPose(pose: BodyPose | null): BodyPoint | null {
  if (!pose || !bodyPresent(pose)) return null;
  const hand = pose.rightIndex ?? pose.rightWrist ?? pose.leftIndex ?? pose.leftWrist;
  const { leftShoulder: ls, rightShoulder: rs } = pose;
  if (!hand || !ls || !rs) return null;
  const shoulderW = Math.abs(rs.x - ls.x);
  const reach = shoulderW * 1.5;
  const cx = (ls.x + rs.x) / 2;
  // Рука в покое ниже плеч: центр зоны — на уровне груди.
  const cy = (ls.y + rs.y) / 2 + shoulderW * 0.5;
  return {
    x: clamp01(0.5 + (hand.x - cx) / (2 * reach)),
    y: clamp01(0.5 + (hand.y - cy) / (2 * reach)),
  };
}

function contains(rect: Rect, p: BodyPoint, grow: number): boolean {
  const gx = rect.w * grow;
  const gy = rect.h * grow;
  return p.x >= rect.x - gx && p.x <= rect.x + rect.w + gx && p.y >= rect.y - gy && p.y <= rect.y + rect.h + gy;
}

/** Кнопка под курсором; уже наведённая проверяется по расширенной рамке. */
export function hitTest(items: readonly MenuItem[], point: BodyPoint | null, hoveredId: string | null): string | null {
  if (!point) return null;
  const hovered = items.find((i) => i.id === hoveredId);
  if (hovered && contains(hovered.rect, point, HOVER_HYSTERESIS)) return hovered.id;
  return items.find((i) => contains(i.rect, point, 0))?.id ?? null;
}

/**
 * Dwell-нажатие: рука на одной кнопке `dwellMs` — нажатие, один раз; повтор — только после ухода с кнопки.
 * До `guardUntilMs` (защитная задержка экрана) отсчёт не идёт. Кнопка, на которой рука уже лежала, когда экран
 * появился, не считается, пока рука с неё не уйдёт: иначе неподвижная рука «проваливается» по экранам подряд.
 */
export class DwellTracker {
  private hoveredId: string | null = null;

  private sinceMs = 0;

  private fired = false;

  private guardUntilMs = 0;

  /** `undefined` — экран только что показан, первый кадр ещё не пришёл; `null` — ничего не заблокировано. */
  private blockedId: string | null | undefined = null;

  constructor(private readonly dwellMs = DWELL_MS) {}

  /** Новый экран: сброс и защитная задержка (уже идущая задержка не укорачивается). */
  reset(nowMs: number, guardMs = 0): void {
    this.hoveredId = null;
    this.fired = false;
    this.guardUntilMs = Math.max(this.guardUntilMs, nowMs + guardMs);
    this.blockedId = undefined;
  }

  /** `progress` 0…1 для индикатора; `fired` — id нажатой кнопки в этом кадре. */
  update(hitId: string | null, nowMs: number): { hoveredId: string | null; progress: number; fired: string | null } {
    if (this.blockedId === undefined) this.blockedId = hitId;
    if (hitId !== this.blockedId) this.blockedId = null;
    if (hitId !== null && hitId === this.blockedId) {
      this.hoveredId = hitId;
      return { hoveredId: hitId, progress: 0, fired: null };
    }
    if (hitId !== this.hoveredId) {
      this.hoveredId = hitId;
      this.sinceMs = nowMs;
      this.fired = false;
    }
    if (!hitId || this.fired) return { hoveredId: hitId, progress: 0, fired: null };
    const start = Math.max(this.sinceMs, this.guardUntilMs);
    const progress = Math.min(1, Math.max(0, (nowMs - start) / this.dwellMs));
    if (progress < 1) return { hoveredId: hitId, progress, fired: null };
    this.fired = true;
    return { hoveredId: hitId, progress: 1, fired: hitId };
  }
}

export type MenuDirection = 'up' | 'down' | 'left' | 'right';

export type RemoteKey = MenuDirection | 'ok' | 'back';

/** Пульт: стрелки, OK и «Назад» (webOS — 461, Tizen — 10009, в браузере — Escape/Backspace). */
export function remoteKey(e: Pick<KeyboardEvent, 'key' | 'keyCode'>): RemoteKey | null {
  switch (e.key) {
  case 'ArrowUp': return 'up';
  case 'ArrowDown': return 'down';
  case 'ArrowLeft': return 'left';
  case 'ArrowRight': return 'right';
  case 'Enter': return 'ok';
  case 'Escape':
  case 'Backspace':
  case 'GoBack':
  case 'BrowserBack':
    return 'back';
  default:
    break;
  }
  if (e.keyCode === 461 || e.keyCode === 10009) return 'back';
  if (e.keyCode === 13) return 'ok';
  return null;
}

/** Фокус D-pad: ближайшая кнопка в нужную сторону (по центрам, со штрафом за уход вбок); края не зациклены. */
export function moveFocus(items: readonly MenuItem[], focusedId: string | null, dir: MenuDirection): string | null {
  const from = items.find((i) => i.id === focusedId);
  if (!from) return items[0]?.id ?? null;
  const center = (r: Rect): BodyPoint => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });
  const c = center(from.rect);
  let best: { id: string; score: number } | null = null;
  items.forEach((item) => {
    if (item.id === from.id) return;
    const p = center(item.rect);
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    const horizontal = dir === 'left' || dir === 'right';
    const sign = dir === 'left' || dir === 'up' ? -1 : 1;
    const along = sign * (horizontal ? dx : dy);
    const across = Math.abs(horizontal ? dy : dx);
    if (along <= 1e-6) return;
    const score = along + across * 2;
    if (!best || score < best.score) best = { id: item.id, score };
  });
  return (best as { id: string } | null)?.id ?? from.id;
}
